import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Card,
  CardContent,
  useTheme,
  Menu,
  ListItemIcon,
  ListItemText,
  Button
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon,
  Assignment as TaskIcon,
  FilterList as FilterIcon,
  TableChart as CsvIcon,
  Code as JsonIcon,
  Clear as ClearIcon,
  Public as TimezoneIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

// 🌐 NEW: Import i18n hooks
import { useTranslation } from 'react-i18next';

// 🆕 NEW: Import smart timezone system
import { useUserTimezone } from '../hooks/useUserTimezone';

import { useAuth } from '../hooks/useAuth';
import { useApiCall, usePaginatedApi } from '../hooks/useApi';
import { api } from '../services/api';
import StatsCard from '../components/dashboard/StatsCard';
import LoadingPage from '../components/common/LoadingPage';
import { formatRelativeTime } from '../utils/formatters';

const AuditPage = () => {
  const theme = useTheme();
  const { hasPermission } = useAuth();
  const [filters, setFilters] = useState({
    action_type: '',
    entity: '',
    user_id: '',
    start_date: null,
    end_date: null
  });
  const [detailDialog, setDetailDialog] = useState({ open: false, log: null });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [exportMenu, setExportMenu] = useState(null);

  // 🌐 NEW: Use translation hook
  const { t } = useTranslation();

  // 🆕 NEW: Use smart timezone system
  const {
    formatUserDateTimeSync,
    timezoneInfo,
    isUserConfigured,
    isBrowserDetected
  } = useUserTimezone();

  // 🆕 ENHANCED: Smart date formatting with user timezone
  const formatSmartDateTime = (timestamp) => {
    if (!timestamp) return '';

    try {
      // Use the smart timezone formatter
      return formatUserDateTimeSync(timestamp, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      console.error('❌ Error formatting timestamp with user timezone:', error, timestamp);

      // Fallback to basic formatting
      try {
        let date;
        if (typeof timestamp === 'string') {
          if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.includes('-', 10)) {
            const utcTimestamp = timestamp.includes('T') ? timestamp + 'Z' : timestamp + 'T00:00:00Z';
            date = new Date(utcTimestamp);
          } else {
            date = new Date(timestamp);
          }
        } else if (typeof timestamp === 'number') {
          date = new Date(timestamp);
        } else {
          date = new Date(timestamp);
        }

        if (isNaN(date.getTime())) {
          console.warn('⚠️ Invalid timestamp format:', timestamp);
          return timestamp.toString();
        }

        const formatter = new Intl.DateTimeFormat('es-ES', {
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });

        return formatter.format(date);
      } catch (fallbackError) {
        console.error('❌ Fallback formatting also failed:', fallbackError);
        return timestamp.toString();
      }
    }
  };

  // API calls
  const {
    data: auditStats,
    loading: statsLoading,
    refetch: refetchStats
  } = useApiCall(api.audit.getStats);

  const {
    data: actionTypes,
    loading: actionTypesLoading
  } = useApiCall(api.audit.getActionTypes);

  const {
    data: entities,
    loading: entitiesLoading
  } = useApiCall(api.audit.getEntities);

  const {
    data: auditLogs,
    loading: logsLoading,
    totalCount,
    params,
    updateParams,
    refetch: refetchLogs,
    goToPage,
    changePageSize
  } = usePaginatedApi(api.audit.getLogs, {
    limit: 25,
    ...filters
  });

  // Check permissions
  if (!hasPermission('manager')) {
    return (
      <Container>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {t('common.error')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('audit.noPermission')}
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Handle filter change with immediate application
  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);

    // Apply filters immediately
    const filterParams = { ...newFilters };

    // Format dates for API - start_date at 00:00, end_date at 23:59
    if (filterParams.start_date) {
      const startDate = new Date(filterParams.start_date);
      startDate.setHours(0, 0, 0, 0);
      filterParams.start_date = startDate.toISOString();
    }
    if (filterParams.end_date) {
      const endDate = new Date(filterParams.end_date);
      endDate.setHours(23, 59, 59, 999);
      filterParams.end_date = endDate.toISOString();
    }

    updateParams(filterParams);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      action_type: '',
      entity: '',
      user_id: '',
      start_date: null,
      end_date: null
    });
    updateParams({});
  };

  // Handle row expansion
  const handleRowToggle = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  // Handle detail view
  const handleDetailView = (log) => {
    setDetailDialog({ open: true, log });
  };

  // Handle row click
  const handleRowClick = (log, event) => {
    if (event.target.closest(t('hardcoded.pages_auditpage.button')) || event.target.closest('.MuiIconButton-root')) {
      return;
    }
    handleDetailView(log);
  };

  // Handle export menu
  const handleExportClick = (event) => {
    setExportMenu(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenu(null);
  };

  // Handle export
  const handleExport = async (format = 'csv') => {
    handleExportClose();

    try {
      const exportParams = { ...filters, format };

      if (exportParams.start_date) {
        const startDate = new Date(exportParams.start_date);
        startDate.setHours(0, 0, 0, 0);
        exportParams.start_date = startDate.toISOString();
      }
      if (exportParams.end_date) {
        const endDate = new Date(exportParams.end_date);
        endDate.setHours(23, 59, 59, 999);
        exportParams.end_date = endDate.toISOString();
      }

      const response = await api.audit.export(exportParams);

      const blob = new Blob([response.data.content], {
        type: response.data.content_type
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(t('hardcoded.pages_auditpage.error_al_exportar'), error);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    refetchStats();
    refetchLogs();
  };

  // 🌐 NEW: Use translations for action types
  const getActionColor = (action) => {
    switch (action) {
      case 'CREATE': return 'success';
      case 'UPDATE': return 'info';
      case 'DELETE': return 'error';
      case 'LOGIN': return 'primary';
      case 'LOGOUT': return 'default';
      default: return 'default';
    }
  };

  const getActionLabel = (action) => {
    return t(`audit.actions.${action}`, action);
  };

  // Render data changes
  const renderDataChanges = (beforeData, afterData) => {
    if (!beforeData && !afterData) return null;

    return (
      <Box sx={{ mt: 2 }}>
        {beforeData && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} color="error.main" sx={{ mb: 1 }}>
              {t('audit.table.before')}:
            </Typography>
            <Paper sx={{ p: 1.5, bgcolor: 'error.50', border: '1px solid', borderColor: 'error.200' }}>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0
                }}
              >
                {JSON.stringify(beforeData, null, 2)}
              </Typography>
            </Paper>
          </Box>
        )}

        {afterData && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="success.main" sx={{ mb: 1 }}>
              {t('audit.table.after')}:
            </Typography>
            <Paper sx={{ p: 1.5, bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  margin: 0
                }}
              >
                {JSON.stringify(afterData, null, 2)}
              </Typography>
            </Paper>
          </Box>
        )}
      </Box>
    );
  };

  if (statsLoading || actionTypesLoading || entitiesLoading) {
    return <LoadingPage message={t('audit.loadingMessage')} />;
  }

  return (
    <Container maxWidth={false} disableGutters>
      {/* Compact Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t('audit.title')}
        </Typography>

      </Box>

      {/* Compact Stats Cards */}
      {auditStats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="h6" color="primary.main" fontWeight={600}>
                {auditStats.total_actions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('audit.stats.totalActions')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="h6" color="info.main" fontWeight={600}>
                {auditStats.unique_users}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('audit.stats.activeUsers')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="h6" color="success.main" fontWeight={600}>
                {auditStats.recent_activity_count}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('audit.stats.last24Hours')}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="h6" color="secondary.main" fontWeight={600}>
                {Object.keys(auditStats.entities || {}).length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('audit.stats.entities')}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Compact Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Filters */}
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              select
              label={t('audit.filters.action')}
              value={filters.action_type}
              onChange={(e) => handleFilterChange('action_type', e.target.value)}
              size="small"
            >
              <MenuItem value="">{t('audit.filters.allActions')}</MenuItem>
              {actionTypes?.map((action) => (
                <MenuItem key={action} value={action}>
                  {getActionLabel(action)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              select
              label={t('audit.filters.entity')}
              value={filters.entity}
              onChange={(e) => handleFilterChange('entity', e.target.value)}
              size="small"
            >
              <MenuItem value="">{t('audit.filters.allEntities')}</MenuItem>
              {entities?.map((entity) => (
                <MenuItem key={entity} value={entity}>
                  {entity}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <DatePicker
              label={t('audit.filters.from')}
              value={filters.start_date}
              onChange={(date) => handleFilterChange('start_date', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2}>
            <DatePicker
              label={t('audit.filters.to')}
              value={filters.end_date}
              onChange={(date) => handleFilterChange('end_date', date)}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12} sm={12} md={4}>
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <Tooltip title={t('audit.filters.clearFilters')}>
                <IconButton
                  onClick={clearFilters}
                  size="small"
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('common.refresh')}>
                <IconButton
                  onClick={handleRefresh}
                  size="small"
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('audit.export.title')}>
                <IconButton
                  onClick={handleExportClick}
                  size="small"
                  sx={{ border: '1px solid', borderColor: 'divider' }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenu}
        open={Boolean(exportMenu)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('audit.export.csv')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleExport('json')}>
          <ListItemIcon>
            <JsonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('audit.export.json')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Compact Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>{t('audit.table.action')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('audit.table.user')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('audit.table.entity')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('audit.table.description')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {t('audit.table.date')}
                  {timezoneInfo && (
                    <Typography variant="caption" display="block" sx={{ color: 'text.secondary', fontWeight: 400 }}>
                      ({timezoneInfo.timezone})
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>-</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logsLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                      {t('common.loading')}
                    </TableCell>
                  </TableRow>
                ))
              ) : auditLogs?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('audit.noRecordsFound')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                auditLogs?.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={(event) => handleRowClick(log, event)}
                    >
                      <TableCell>
                        <Chip
                          label={getActionLabel(log.action_type)}
                          color={getActionColor(log.action_type)}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {log.user_name || t('hardcoded.components_ai_aimemoryeditor.sistema')}
                          </Typography>
                          {log.ip_address && (
                            <Typography variant="caption" color="text.secondary">
                              {log.ip_address}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {log.entity}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: { xs: 120, sm: 200 },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {log.action_description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {formatSmartDateTime(log.created_at)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(log.created_at)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={t('audit.table.view')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDetailView(log);
                              }}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={expandedRows.has(log.id) ? t('audit.table.collapse') : t('audit.table.expand')}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowToggle(log.id);
                              }}
                            >
                              {expandedRows.has(log.id) ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                        <Collapse in={expandedRows.has(log.id)} timeout="auto" unmountOnExit>
                          <Box sx={{ m: 1 }}>
                            <Card elevation={0} sx={{ bgcolor: 'grey.50' }}>
                              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="body2" color="text.secondary">
                                      <strong>{t('audit.detail.id')}:</strong> {log.entity_id || t('hardcoded.pages_auditpage.na')}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                      <strong>{t('audit.table.browser')}:</strong>
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{
                                        fontFamily: 'monospace',
                                        wordBreak: 'break-word',
                                        display: 'block',
                                        mt: 0.5,
                                        fontSize: '0.7rem'
                                      }}
                                    >
                                      {log.user_agent || t('hardcoded.pages_auditpage.na')}
                                    </Typography>
                                  </Grid>
                                </Grid>
                                {(log.before_data || log.after_data) &&
                                  renderDataChanges(log.before_data, log.after_data)
                                }
                              </CardContent>
                            </Card>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount || 0}
          page={params.page - 1}
          onPageChange={(_, newPage) => goToPage(newPage + 1)}
          rowsPerPage={params.limit}
          onRowsPerPageChange={(e) => changePageSize(parseInt(e.target.value))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('hardcoded.components_schematics_receivereventsmodal.por_pagina')}
          labelDisplayedRows={({ from, to, count }) =>
            t('hardcoded.pages_auditpage.displayed_rows', {
              from,
              to,
              count: count !== -1 ? count : t('hardcoded.components_alarms_alarmlist.mas_de_to', { to })
            })
          }
          size="small"
        />
      </Paper>

      {/* Compact Detail Dialog */}
      <Dialog
        open={detailDialog.open}
        onClose={() => setDetailDialog({ open: false, log: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            {t('audit.detail.title')}
          </Typography>
          {/* 🆕 NEW: Timezone info in detail dialog */}
          {timezoneInfo && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.zona_horaria')}{timezoneInfo.name} ({timezoneInfo.offsetString})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {detailDialog.log && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t('audit.detail.basicInfo')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('audit.table.action')}:</strong> {getActionLabel(detailDialog.log.action_type)}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('audit.table.user')}:</strong> {detailDialog.log.user_name || t('hardcoded.components_ai_aimemoryeditor.sistema')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('audit.table.entity')}:</strong> {detailDialog.log.entity}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('audit.detail.id')}:</strong> {detailDialog.log.entity_id || t('hardcoded.pages_auditpage.na')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('audit.table.date')}:</strong> {formatSmartDateTime(detailDialog.log.created_at)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {t('audit.detail.technicalDetails')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('audit.detail.ip')}:</strong> {detailDialog.log.ip_address || t('hardcoded.pages_auditpage.na')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>{t('audit.table.browser')}:</strong>
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontFamily: 'monospace',
                      wordBreak: 'break-word',
                      backgroundColor: 'grey.100',
                      p: 1,
                      borderRadius: 0.5,
                      display: 'block',
                      fontSize: '0.7rem'
                    }}
                  >
                    {detailDialog.log.user_agent || t('hardcoded.pages_auditpage.na')}
                  </Typography>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                {t('audit.table.description')}
              </Typography>
              <Paper sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                <Typography variant="body2">
                  {detailDialog.log.action_description}
                </Typography>
              </Paper>

              {(detailDialog.log.before_data || detailDialog.log.after_data) && (
                <>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                    {t('audit.detail.dataChanges')}
                  </Typography>
                  {renderDataChanges(detailDialog.log.before_data, detailDialog.log.after_data)}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetailDialog({ open: false, log: null })}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AuditPage;